<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ChevronLeft } from '@lucide/vue';
import { usePlatform } from '@core/platform';
import { useNavigation } from '@app/composables';
import { useFlowProgress } from '@app/composables/useFlowProgress';
import PaperStrip from '@app/presentation/components/paper/PaperStrip.vue';

// Header for immersive (chrome: 'bare') flows. A flush, opaque top bar carrying
// an in-app back chevron (web/PWA only — Telegram uses its native BackButton)
// and either the step-progress segments (multi-step flows) or the route title.
//
// Mounted as a non-scrolling sibling ABOVE the scroll <main> in both shells, so
// content can never bleed above it. Opaque background (no translucency) for the
// same reason.
const route = useRoute();
const { t } = useI18n();
const { isTelegram } = usePlatform();
const { goUp } = useNavigation();
const { count, current } = useFlowProgress();

const hasParent = computed(() => typeof route.meta.parent === 'string');
const hasSteps = computed(() => count.value > 1);

// 1-based progress for the segments (current is the 0-based active step index).
const progress = computed(() => current.value + 1);

const title = computed(() =>
  typeof route.meta.title === 'string' ? t(route.meta.title) : '',
);

// Only the web/PWA build shows the in-app chevron — Telegram uses its native
// BackButton (locked decision #1).
const showChevron = computed(() => !isTelegram && hasParent.value);

// Render on bare flows that have a parent OR an active multi-step flow, but only
// when the bar would actually carry something (chevron / segments / title) — this
// keeps an empty bar (e.g. a single-step reading on Telegram, where back is
// native and there's no title) from eating layout height.
const visible = computed(
  () =>
    route.meta.chrome === 'bare' &&
    (hasParent.value || hasSteps.value) &&
    (showChevron.value || hasSteps.value || title.value !== ''),
);
</script>

<template>
  <!-- Torn paper strip (PaperStrip: card stock, torn bottom edge, lit fibre +
       cast shade). The strip is opaque stock; only the ≤6px tear lets the page
       show between its teeth. -->
  <div
    v-if="visible"
    class="relative isolate z-40 flex h-12 shrink-0 items-center gap-1 px-2"
    style="padding-top: env(safe-area-inset-top)"
  >
    <PaperStrip edge="bottom" />

    <!-- LEFT: in-app back chevron — web/PWA only (Telegram uses native back). -->
    <button
      v-if="showChevron"
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--paper-radius-xs)] text-foreground transition-[background-color,translate] hover:bg-foreground/8 active:translate-y-px motion-reduce:transition-none motion-reduce:active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-stock/50"
      :aria-label="t('nav.back')"
      @click="goUp"
    >
      <ChevronLeft class="h-5 w-5" />
    </button>
    <span v-else class="h-9 w-9 shrink-0" aria-hidden="true" />

    <!-- CENTER: step progress as small paper tabs (multi-step) or the route title.
         done = cream stock, slightly faded · current = full cream, taller, lifted,
         with an ink rim + cast · todo = inset well. -->
    <div class="flex min-w-0 flex-1 items-center justify-center px-1">
      <div
        v-if="hasSteps"
        class="flex h-3 w-full max-w-[18rem] flex-row items-center gap-1.5"
      >
        <span
          v-for="index in count"
          :key="index"
          class="step-tab flex-1 transition-[background-color,box-shadow,translate,opacity] duration-300 motion-reduce:transition-none"
          :class="index < progress
            ? 'paper-tag h-2 opacity-70'
            : index === progress
              ? 'paper-tag h-2.5 -translate-y-0.5 shadow-[0_1px_0_rgb(4_3_18/0.55),0_3px_5px_-2px_var(--paper-shade)] ring-1 ring-inset ring-paper-stock-ink/30'
              : 'h-2 bg-muted shadow-[inset_0_1px_2px_var(--paper-shade)] ring-1 ring-inset ring-foreground/15'"
        />
      </div>
      <span
        v-else-if="title"
        class="truncate type-label text-foreground"
      >
        {{ title }}
      </span>
    </div>

    <!-- RIGHT: spacer to keep the center balanced against the left chevron. -->
    <span class="h-9 w-9 shrink-0" aria-hidden="true" />
  </div>
</template>

<style scoped>
/* Small hand-cut tabs — a scissor clip-path would eat an 8px-tall piece, so
   the irregularity comes from uneven corner radii instead. */
.step-tab {
  border-radius: 3px 2px 3px 1.5px / 2px 3px 1.5px 2.5px;
}
</style>
